import { useMemo, useState } from "react";
import NewTicketModal, { newTicketObjType } from "./Modal/NewTicketModal";
import AttToken from "../../helpers/attToken";
import NovoChamado from "../../service/NovoChamado";
import { useTranslation } from "react-i18next";
import { RiDeleteBin5Line, RiPencilFill } from "react-icons/ri";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { tableOptions } from '../TableHelpers/options';
import { StyledDiv } from "../TableHelpers/TableStyle";
import { toast } from "react-toastify";
import queryClient from "../../service/query";
import { ThemeProvider } from '@mui/material/styles';
import { getMuiTheme } from "../TableHelpers/options";
import { trackEventMatomo } from "../../helpers/matomo";
import { Button, Skeleton, Stack } from "@mui/material";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import QueryFn from "../../helpers/QueryFn";

const queryFn = new QueryFn();

const initialValue = {
  name: '',
  message: '',
  serviceType: '',
  daysLimit: null,
  timeLimit: null,
  setsList: [],
  responsible: '',
  redirectable: false
};


export default function NewTickets() {
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [ticketEdit, setTicketEdit] = useState<newTicketObjType | undefined>(initialValue);

  const chamadoService = useMemo(() => new NovoChamado(), []);
  const { t } = useTranslation('translation');

  const response = queryFn.useFetch('GET_MODEL_LIST',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const response = await chamadoService.getList(token);
          return response;
        }
      } catch (err) {
        return err;
      }
    }
  );

  const deletaFn = queryFn.useMutate('DELETE_TICKET', async (value: string) => {
    try {
      const token = await AttToken();
      if (token) {
        const response = await chamadoService.deleteModel(token, value);
        queryClient.resetQueries({ queryKey: ['GET_MODEL_LIST'] });
        toast.success(`${t('notifications.ticket.Modeloremovido')}`);
        return response;
      }
    } catch (err) {
      toast.error(`${t('notifications.ticket.Erroaoremover')}`);
      return err;
    }
  });

  const columns = [
    {
      name: "name",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "message",
      label: t('message'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        customBodyRender: (value: string,) => {
          return (
            <Button
              endIcon={<RiPencilFill />}
              onClick={() => {
                trackEventMatomo('tela de chamado 2.0', 'click', 'button', 'abre modal');
                setOpenModal(!openModal);
                setIsEdit(true);
                const ticket = response?.data?.find((elem: newTicketObjType) => elem.id === value);
                setTicketEdit(ticket);
              }}
              className="button"
            >
              {t('edit')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
    {
      name: "id",
      label: t('delete'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiDeleteBin5Line />}
              onClick={() => {
                trackEventMatomo('tela de chamado 2.0', 'click', 'button', 'deleta');
                deletaFn.mutateAsync(value);
              }} className="button is-danger"
            >
              {t('delete')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  return (
    <>
      {openModal &&
        <NewTicketModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          ticketEdit={ticketEdit}
          setTicketEdit={setTicketEdit}
        />}
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        <Button
          disabled={response?.isLoading}
          endIcon={<AddCircleIcon />}
          className="button is-primary"
          type="button"
          name='add'
          onClick={() => {
            trackEventMatomo('tela de chamado 2.0', 'click', 'button', 'abre modal');
            setTicketEdit(initialValue);
            setOpenModal(!openModal);
          }}
        >
          {t('notifications.ticket.novoModelo')}
        </Button>
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          width={'100%'}
        >
          {response?.isLoading
            ? <Skeleton variant="rounded" width={'100%'} height={400} />
            : <StyledDiv>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={t('notifications.ticket.chamadoModeloNovo')}
                  data={response?.data}
                  columns={columns}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            </StyledDiv>}
        </Stack>
      </Stack>
    </>
  );
}